import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    BuiltInExternalPhotoSourceConfig,
    BuiltInExternalPhotoSourceDefinition,
    CreateBuiltInExternalPhotoSourceInput,
    CreateDirectPhotoSourceInput,
    CreateGenericExternalPhotoSourceInput,
    DeleteBuiltInExternalPhotoSourceInput,
    DeleteGenericExternalPhotoSourceInput,
    DirectPhotoSource,
    GenericExternalPhotoSourceConfig,
    GraphDotnetService,
    graphql,
    MediaclipMutationUpdateStoreDesignerAssetArgs,
    SessionPhotoSource,
    StockPhotoSourceConfig,
    StoreDesignerAsset,
    UpdateBuiltInExternalPhotoSourceInput,
    UpdateDirectPhotoSourceInput,
    UpdateGenericExternalPhotoSourceInput,
    UpdateStoreDesignerAssetInput,
} from 'app/shared/graph';

export type PhotoSourceBasicSettings = {
    isRecentUploadsEnabled: boolean;
    isExternalUploaderEnabled: boolean;
    isPastUploadsEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class ExternalPhotosConfigService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    // region Basic Settings

    getPhotoSourceBasicSetting(storeId: string): Observable<PhotoSourceBasicSettings> {
        return this.graphDotnetService.query(graphql`
                query getStoreSettings($storeId: String!) {
                    store(id: $storeId) {
                        label
                        config {
                            isRecentUploadsEnabled
                            isPastUploadsEnabled
                            isExternalUploaderEnabled
                        }
                    }
                }
            `,
            {
                storeId,
            },
        ).pipe(map(q => ({
            isRecentUploadsEnabled: q.store.config.isRecentUploadsEnabled ?? false,
            isPastUploadsEnabled: q.store.config.isPastUploadsEnabled,
            isExternalUploaderEnabled: q.store.config.isExternalUploaderEnabled ?? false,
        })));
    }

    // endregion

    // region External Photo Source

    loadExternalPhotoSources(storeId: string): Observable<{
        builtInExternalPhotoSources: Array<BuiltInExternalPhotoSourceConfig>,
        genericExternalPhotoSources: Array<GenericExternalPhotoSourceConfig>,
        stockPhotoSources: Array<StockPhotoSourceConfig>
    }> {
        return this.graphDotnetService.query(graphql`
                query loadExternalPhotoSources($storeId: String!) {
                    store(id: $storeId) {
                        builtInExternalPhotoSources {
                            store {
                                id
                            }
                            photoSourceId
                            label
                            details
                            status
                            icon
                            appId
                        }
                        genericExternalPhotoSources {
                            store {
                                id
                            }
                            photoSourceId
                            label
                            status
                            icon
                            appId
                            scopes
                            authorizationUrl
                            tokenUrl
                            albumUrl
                            photoUrl
                        }
                        stockPhotoSources {
                            id
                            label
                            thumbnailUrl
                            photosEndpoint {
                                url
                            }
                            albumsEndpoint {
                                url
                            }
                        }
                    }
                }
            `, { storeId: storeId },
        ).pipe(map((result) => ({
            builtInExternalPhotoSources: result.store!.builtInExternalPhotoSources,
            genericExternalPhotoSources: result.store!.genericExternalPhotoSources,
            stockPhotoSources: result.store!.stockPhotoSources,
        })));
    }

    // endregion

    // region Generic External Photo Source

    loadGenericExternalPhotoSource(storeId: string, photoSourceId: string): Observable<GenericExternalPhotoSourceConfig | undefined> {
        return this.graphDotnetService.query(graphql`
                query loadGenericExternalPhotoSource($storeId: String!) {
                    store(id: $storeId) {
                        genericExternalPhotoSources {
                            store {
                                id
                            }
                            photoSourceId
                            label
                            status
                            icon
                            appId
                            scopes
                            authorizationUrl
                            tokenUrl
                            albumUrl
                            photoUrl
                        }
                    }
                }
            `, { storeId: storeId },
        ).pipe(
            map((result) => result.store!.genericExternalPhotoSources.find(s => s.photoSourceId == photoSourceId)),
        );
    }


    createGenericExternalPhotoSource(input: CreateGenericExternalPhotoSourceInput): Observable<GenericExternalPhotoSourceConfig> {
        return this.graphDotnetService.mutate(graphql`
            mutation createGenericExternalPhotoSource($input: CreateGenericExternalPhotoSourceInput!) {
                createGenericExternalPhotoSource(input: $input) {
                    store {
                        id
                    }
                    photoSourceId
                    label
                    details
                    icon
                    status
                    appId
                    scopes
                    authorizationUrl
                    tokenUrl
                    albumUrl
                    photoUrl
                }
            }
        `, {
            input,
        }).pipe(
            map(m => m.createGenericExternalPhotoSource!),
        );
    }

    updateGenericExternalPhotoSource(input: UpdateGenericExternalPhotoSourceInput): Observable<GenericExternalPhotoSourceConfig> {
        return this.graphDotnetService.mutate(graphql`
            mutation updateGenericExternalPhotoSource($input: UpdateGenericExternalPhotoSourceInput!) {
                updateGenericExternalPhotoSource(input: $input) {
                    store {
                        id
                    }
                    photoSourceId
                    label
                    details
                    icon
                    status
                    appId
                    scopes
                    authorizationUrl
                    tokenUrl
                    albumUrl
                    photoUrl
                }
            }
        `, {
            input,
        }).pipe(
            map(m => m.updateGenericExternalPhotoSource!),
        );
    }

    deleteGenericExternalPhotoSource(input: DeleteGenericExternalPhotoSourceInput): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation deleteGenericExternalPhotoSource($input: DeleteGenericExternalPhotoSourceInput!) {
                deleteGenericExternalPhotoSource(input: $input)
            }
        `, {
            input,
        }).pipe(
            map(m => m.deleteGenericExternalPhotoSource!),
        );
    }

    // endregion

    // region Built-In External Photo Source

    loadBuiltInExternalPhotoSourceDefinitions(): Observable<Array<BuiltInExternalPhotoSourceDefinition>> {
        return this.graphDotnetService.query(graphql`
                query {
                    builtInExternalPhotoSourceDefinitions {
                        label
                        providerId
                        iconUrl
                    }
                }
            `,
        ).pipe(map((result) => result.builtInExternalPhotoSourceDefinitions));
    }

    loadBuiltInExternalPhotoSource(storeId: string, photoSourceId: string) {
        return this.graphDotnetService.query(graphql`
                query loadBuiltInExternalPhotoSources($storeId: String!) {
                    store(id: $storeId) {
                        builtInExternalPhotoSources {
                            store {
                                id
                            }
                            photoSourceId
                            label
                            status
                            icon
                            appId
                        }
                    }
                }
            `, { storeId: storeId },
        ).pipe(
            map((result) => result.store!.builtInExternalPhotoSources.find(s => s.photoSourceId == photoSourceId)),
        );
    }

    createBuiltInExternalPhotoSource(input: CreateBuiltInExternalPhotoSourceInput): Observable<BuiltInExternalPhotoSourceConfig> {
        return this.graphDotnetService.mutate(graphql`
            mutation createBuiltInExternalPhotoSource($input: CreateBuiltInExternalPhotoSourceInput!) {
                createBuiltInExternalPhotoSource(input: $input) {
                    store {
                        id
                    }
                    photoSourceId
                    label
                    details
                    icon
                    status
                    appId
                }
            }
        `, {
            input,
        }).pipe(
            map(m => m.createBuiltInExternalPhotoSource!),
        );
    }

    updateBuiltInExternalPhotoSource(input: UpdateBuiltInExternalPhotoSourceInput): Observable<BuiltInExternalPhotoSourceConfig> {
        return this.graphDotnetService.mutate(graphql`
            mutation updateBuiltInExternalPhotoSource($input: UpdateBuiltInExternalPhotoSourceInput!) {
                updateBuiltInExternalPhotoSource(input: $input) {
                    store {
                        id
                    }
                    photoSourceId
                    label
                    details
                    icon
                    status
                    appId
                }
            }
        `, {
            input,
        }).pipe(
            map(m => m.updateBuiltInExternalPhotoSource!),
        );
    }

    deleteBuiltInExternalPhotoSource(input: DeleteBuiltInExternalPhotoSourceInput): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation deleteBuiltInExternalPhotoSource($input: DeleteBuiltInExternalPhotoSourceInput!) {
                deleteBuiltInExternalPhotoSource(input: $input)
            }
        `, {
            input,
        }).pipe(
            map(m => m.deleteBuiltInExternalPhotoSource!),
        );
    }

    // endregion

    // region Direct Photo Source

    loadDirectPhotoSources(storeId: string): Observable<DirectPhotoSource[]> {
        return this.graphDotnetService.query(graphql`
                query loadDirectPhotoSources($storeId: String!) {
                    store(id: $storeId) {
                        directPhotoSources {
                            name
                            photoEndpoint {
                                url
                                headers {
                                    name
                                    value
                                }
                            }
                        }
                    }
                }
            `, { storeId: storeId },
        ).pipe(
            map((result) => result.store!.directPhotoSources),
        );
    }

    loadDirectPhotoSource(storeId: string, directPhotoSourceId: string): Observable<DirectPhotoSource | undefined> {
        return this.loadDirectPhotoSources(storeId).pipe(map(sources => sources.find(s => s.name == directPhotoSourceId)));
    };

    createDirectPhotoSource(input: CreateDirectPhotoSourceInput): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation createDirectPhotoSource($input: CreateDirectPhotoSourceInput!) {
                createDirectPhotoSource(input: $input)
            }
        `, {
            input,
        }).pipe(
            map(m => m.createDirectPhotoSource!),
        );
    }

    updateDirectPhotoSource(input: UpdateDirectPhotoSourceInput): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation updateDirectPhotoSource($input: UpdateDirectPhotoSourceInput!) {
                updateDirectPhotoSource(input: $input)
            }
        `, {
            input,
        }).pipe(
            map(m => m.createDirectPhotoSource!),
        );
    }

    // endregion

    // region Session Photo Sources

    loadSessionPhotoSources(storeId: string): Observable<SessionPhotoSource[]> {
        return this.graphDotnetService.query(graphql`
                query loadSessionPhotoSources($storeId: String!) {
                    store(id: $storeId) {
                        sessionPhotoSources  {
                            name
                            thumbnailUrl
                            enabled
                        }
                    }
                }
            `, { storeId: storeId },
        ).pipe(
            map((result) => result.store!.sessionPhotoSources),
        );
    }

    loadSessionPhotoSource(storeId: string, name: string): Observable<SessionPhotoSource | undefined> {
        return this.loadSessionPhotoSources(storeId).pipe(map(sources => sources.find(s => s.name === name)));
    }

    createSessionPhotoSource(input: { storeId: string, name: string, thumbnailUrl: string, enabled: boolean }): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation createSessionPhotoSource($input: CreateSessionPhotoSourceInput!) {
                createSessionPhotoSource(input: $input)
            }
        `, { input }).pipe(map(m => m.createSessionPhotoSource!));
    }

    updateSessionPhotoSource(input: {
        storeId: string,
        name: string,
        thumbnailUrl: string,
        enabled: boolean
    }): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation updateSessionPhotoSource($input: UpdateSessionPhotoSourceInput!) {
                updateSessionPhotoSource(input: $input)
            }
        `, {
            input,
        }).pipe(
            map(m => m.updateSessionPhotoSource!),
        );
    }

    // endregion

    // region Store Assets

    uploadAssetEditorFile(input: UpdateStoreDesignerAssetInput): Observable<StoreDesignerAsset> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreDesignerAssetArgs>(
            graphql`
                mutation updateStoreDesignerCustomAsset($input: UpdateStoreDesignerAssetInput!) {
                    updateStoreDesignerAsset(input: $input) {
                        contentHash
                        filename
                        mimeType
                        publicUrl
                        url
                    }
                }
            `,
            { input },
        ).pipe(
            map((result) => result.updateStoreDesignerAsset),
        );
    }

    // endregion
}
